import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21 } from "styled-components";
import React, { useRef } from "react";
import Marquee from "react-fast-marquee";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { VideoControls } from "~/features/ui/video/video-controls";
import { ArticleTeaser } from "~/features/hero/secondary-hero/article-teaser";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { Slider, type Shape } from "~/features/ui/slider";
import { IconButton } from "~/features/ui/icon-button";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { isLinkValid } from "~/features/ui/link/utils";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { RichText } from "~/features/ui/rich-text";
import { Tag } from "~/features/ui/tag";
import CaretLeft from "~/public/icons/caret-left.svg";
import CaretRight from "~/public/icons/caret-right.svg";
import CaretDown from "~/public/icons/caret-down.svg";
import { Icon } from "~/features/ui/icon";
const fragment = graphql(`
  fragment SecondaryHero on hero_secondaryHero_BlockType {
    tag: baseText3
    headline: baseHeadline
    caption: baseRichText
    media: baseMedia {
      ...Media
    }
    link: baseLink {
      ...Link
    }
    supportLink: baseLink2 {
      ...Link
    }
    articles: articleTeasers {
      ...ArticleTeaserWithImage
    }
    widgetImage: baseImage {
      ...Asset
    }
    widgetDate: baseText
    widgetTitle: baseText2
    widgetUrl: baseUrl
  }
`);

// Design ("Hero / Secondary"): a soft bottom shade for the copy. The light top shade only sits behind
// the transparent nav so its white links stay readable on bright images.
const containerStyle = {
  "&::before": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "left": "0",
    "right": "0",
    "top": "0",
    "zIndex": "10",
    "height": "24rem",
    "backgroundImage": "linear-gradient(180deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0) 100%)",
    "--tw-content": "\"\""
  },
  "&::after": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "inset": "0",
    "backgroundImage": "linear-gradient(184deg, rgba(0, 0, 0, 0) 44%, rgba(0, 0, 0, 0.4) 82%)",
    "--tw-content": "\"\""
  }
};
const discoverBounce = /*#__PURE__*/_css(["display:inline-flex;@keyframes secondaryHeroDiscoverBounce{0%,100%{transform:translateY(0);}50%{transform:translateY(2px);}}@media (prefers-reduced-motion:no-preference){animation:secondaryHeroDiscoverBounce 1s ease-in-out infinite;}"]);
export function SecondaryHero(props: FragmentType<typeof fragment>) {
  const {
    tag,
    headline,
    caption,
    media: fullscreenMedia,
    articles = [],
    link: _link,
    supportLink: _supportLink,
    widgetImage: _widgetImage,
    widgetDate,
    widgetTitle,
    widgetUrl
  } = useFragment(fragment, props);
  const widgetImage = takeFirst(_widgetImage);
  const widgetSlide = widgetTitle && widgetUrl ? {
    id: "widget-slide",
    url: widgetUrl,
    title: widgetTitle,
    date: widgetDate || new Date().toISOString(),
    legacyImage: widgetImage ? [widgetImage] : [],
    hero: []
  } as (typeof articles)[number] : null;
  const slides = widgetSlide ? [widgetSlide, ...articles] : articles;
  const link = takeFirst(_link);
  const supportLink = takeFirst(_supportLink);
  const heroRootRef = useRef<HTMLDivElement>(null);
  const handleScrollDown = () => {
    const next = heroRootRef.current?.nextElementSibling;
    if (next) next.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });else window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth"
    });
  };
  const firstMedia = takeFirst(fullscreenMedia);
  const isInlineVideo = firstMedia && ("videoUrl" in firstMedia && !!firstMedia.videoUrl || "videoAsset" in firstMedia && hasItems(firstMedia.videoAsset));
  const icon = useLinkIcon(link || undefined);
  const blurRef = React.useRef<HTMLDivElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const sliderRef = useRef<Shape>(null);
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const darkStyle = getThemeStyle({
    mode: "dark"
  });
  const lightStyle = getThemeStyle({
    mode: "light"
  });
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const isLg = useMediaQuery(up("lg"));
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--header-height) + var(--announcement-bar-height) + 56px)",
    "@media (min-width: 1024px)": {
      "marginTop": "calc(var(--header-height) + var(--announcement-bar-height))"
    }
  } : {
    "marginTop": "calc(var(--header-height) + 56px)",
    "@media (min-width: 1024px)": {
      "marginTop": "var(--header-height)"
    }
  };
  React.useEffect(() => {
    if (!blurRef.current) {
      return;
    }
    const onScroll = () => {
      const blurValue = Math.min(8, window.scrollY / 100);
      const filterValue = `blur(${blurValue}px)`;
      blurRef.current!.style.backdropFilter = filterValue;
      // @ts-expect-error
      blurRef.current!.style.webkitBackdropFilter = filterValue;
    };
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  return <_StyledDiv ref={heroRootRef} $_css2={[{
    "borderTopWidth": "0px !important"
  }, lightStyle.background]}>
      <_StyledDiv2 $_css3={[{
      "position": "relative",
      "zIndex": "10",
      "@media (min-width: 1024px)": {
        "paddingBottom": "0"
      }
    }, darkStyle.text]}>
        <_StyledDiv3 ref={blurRef} />
        <_StyledDiv4 $_css4={[{
        "position": "relative",
        "display": "grid",
        "minHeight": "100svh",
        "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
        "gridTemplateRows": "repeat(1, minmax(0, 1fr))",
        "alignItems": "flex-end",
        "gap": "var(--spacing-lg)",
        "@media (min-width: 1024px)": {
          "minHeight": "100svh",
          "gridTemplateColumns": "1fr 300px"
        }
      }, articles.length > 0 && {
        "minHeight": "calc(100svh + 12.8rem)"
      }, containerStyle, darkStyle.border]}>
          <_StyledDiv5>
            <Media {...takeFirst(fullscreenMedia)} imageProps={{
            fit: "cover",
            rounded: false,
            loading: "eager",
            priority: true,
            fetchPriority: "high",
            sizes: "100vw"
          }} inlineVideoProps={{
            fit: "cover",
            videoRef: fullscreenVideoRef,
            rounded: false,
            priority: true
          }} externalVideoProps={{
            fit: "cover",
            rounded: false,
            background: true
          }} />
          </_StyledDiv5>
          <_StyledDiv6 $_css5={[{
          "position": "relative",
          "zIndex": "10",
          "display": "flex",
          "flex": "1 1 0%",
          "flexDirection": "column",
          "gap": "0.8rem",
          "paddingLeft": "4rem",
          "paddingRight": "4rem",
          "paddingTop": "2.4rem",
          "paddingBottom": "2.4rem",
          "@media (min-width: 1024px)": {
            "gap": "1.6rem",
            "paddingLeft": "8rem",
            "paddingRight": "8rem",
            "paddingTop": "4rem",
            "paddingBottom": "4rem"
          }
        }, topSpacing]}>
            <_StyledDiv7>
              {tag && <Tag variant="blur">{tag}</Tag>}
              {headline && <_StyledHeadline text={headline} />}
            </_StyledDiv7>
            {caption && <_StyledDiv8>
                <RichText text={caption} />
              </_StyledDiv8>}
            <_StyledDiv9>
              {isInlineVideo && <VideoControls size="medium" variant="tertiary" videoRef={fullscreenVideoRef} />}
              <_StyledDiv10>
                {isLinkValid(link) && <Button as={Link} {...link} variant="secondary" icon={icon} />}
                {isLinkValid(supportLink) && <Button as={Link} {...supportLink} variant="underline" />}
              </_StyledDiv10>
            </_StyledDiv9>
          </_StyledDiv6>
          {articles.length > 0 && <_StyledDiv11>
              {!isLg && <div>
                  {slides.length > 1 ? <Marquee speed={30} gradient={false} pauseOnHover>
                      {slides.map((article, idx) => <_StyledDiv12 key={idx}>
                          <ArticleTeaser {...article} newWindow={idx === 0 && !!widgetSlide} />
                        </_StyledDiv12>)}
                    </Marquee> : <_StyledDiv13>
                      {slides.map((article, idx) => <div key={idx}>
                          <ArticleTeaser {...article} newWindow={idx === 0 && !!widgetSlide} />
                        </div>)}
                    </_StyledDiv13>}
                </div>}

              {isLg && <div>
                  <Slider ref={sliderRef} options={{
              type: "slide",
              perPage: 1,
              gap: 2,
              arrows: false,
              pagination: false,
              drag: true,
              rewind: true,
              autoplay: true,
              loop: true,
              interval: 8000,
              breakpoints: {
                1024: {
                  gap: 8
                }
              }
            }} onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())}>
                    {slides.map((article, idx) => <Slider.Slide key={idx}>
                        <ArticleTeaser {...article} newWindow={idx === 0 && !!widgetSlide} />
                      </Slider.Slide>)}
                  </Slider>
                </div>}
              {slides.length > 1 && <_StyledDiv14>
                  <_StyledDiv15>
                    {slides.map((_, idx) => <_StyledButton key={idx} onClick={() => sliderRef.current?.go(idx)} aria-label={`Go to article ${idx + 1}`} $_css6={[{
                "height": "0.8rem",
                "width": "0.8rem",
                "transitionProperty": "background-color",
                "transitionDuration": "250ms"
              }, activeSlideIndex === idx ? {
                "--tw-bg-opacity": "1",
                "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
              } : {
                "backgroundColor": "rgb(255 255 255 / 0.3)",
                "&:hover": {
                  "--tw-bg-opacity": "1",
                  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
                }
              }]} />)}
                  </_StyledDiv15>

                  <_StyledDiv16>
                    <IconButton variant="transparent" size="xsmall" icon={{
                component: CaretLeft
              }} onClick={() => sliderRef.current?.go("<")} aria-label="Previous article" />
                    <IconButton variant="transparent" size="xsmall" icon={{
                component: CaretRight
              }} onClick={() => sliderRef.current?.go(">")} aria-label="Next article" />
                  </_StyledDiv16>
                </_StyledDiv14>}
            </_StyledDiv11>}
          {slides.length === 0 && <_StyledButton2 type="button" onClick={handleScrollDown} aria-label="Scroll to next section" $_css7={[{
          "position": "relative",
          "zIndex": "10",
          "display": "flex",
          "flexDirection": "row",
          "alignItems": "center",
          "justifyContent": "flex-start",
          "gap": "0.8rem",
          "paddingLeft": "4rem",
          "paddingRight": "4rem",
          "paddingBottom": "2.4rem",
          "color": "currentColor",
          "transitionProperty": "opacity",
          "transitionDuration": "500ms",
          "&:hover": {
            "opacity": "0.7"
          },
          "@media (min-width: 1024px)": {
            "position": "absolute",
            "bottom": "4rem",
            "right": "8rem",
            "paddingLeft": "0",
            "paddingRight": "0",
            "paddingBottom": "0"
          }
        }]}>
              <_StyledSpan>Discover more</_StyledSpan>
              <_StyledSpan2>
                <Icon icon={CaretDown} size="16" />
              </_StyledSpan2>
            </_StyledButton2>}
        </_StyledDiv4>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1ftbzj0-0"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1ftbzj0-1"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1ftbzj0-2"
})({
  "position": "absolute",
  "inset": "0",
  "zIndex": "10"
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1ftbzj0-3"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-1ftbzj0-4"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledDiv6 = _styled6("div").withConfig({
  componentId: "sc-sf-1ftbzj0-5"
})`${p => p.$_css5}`;
var _StyledDiv7 = _styled7("div").withConfig({
  componentId: "sc-sf-1ftbzj0-6"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start"
});
var _StyledHeadline = _styled8(Headline).withConfig({
  componentId: "sc-sf-1ftbzj0-7"
})({
  "maxWidth": "22ch",
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "5.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.054000000000000006rem",
    "fontWeight": "400"
  }
});
var _StyledDiv8 = _styled9("div").withConfig({
  componentId: "sc-sf-1ftbzj0-8"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "maxWidth": "64rem",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv9 = _styled10("div").withConfig({
  componentId: "sc-sf-1ftbzj0-9"
})({
  "display": "flex",
  "alignItems": "center",
  "gap": "var(--spacing-sm)"
});
var _StyledDiv10 = _styled11("div").withConfig({
  componentId: "sc-sf-1ftbzj0-10"
})({
  "display": "flex",
  "alignItems": "center",
  "gap": "var(--spacing-md)"
});
var _StyledDiv11 = _styled12("div").withConfig({
  componentId: "sc-sf-1ftbzj0-11"
})({
  "position": "relative",
  "zIndex": "10",
  "paddingLeft": "0",
  "paddingRight": "0",
  "paddingBottom": "2.4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "0",
    "paddingRight": "4rem",
    "paddingBottom": "4rem"
  }
});
var _StyledDiv12 = _styled13("div").withConfig({
  componentId: "sc-sf-1ftbzj0-12"
})({
  "width": "320px",
  "paddingLeft": "0.8rem"
});
var _StyledDiv13 = _styled14("div").withConfig({
  componentId: "sc-sf-1ftbzj0-13"
})({
  "paddingLeft": "0.8rem",
  "paddingRight": "0.8rem"
});
var _StyledDiv14 = _styled15("div").withConfig({
  componentId: "sc-sf-1ftbzj0-14"
})({
  "marginTop": "0.2rem",
  "display": "none",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "1.6rem",
  "overflow": "hidden",
  "borderRadius": "4px",
  "backgroundColor": "rgb(255 255 255 / 0.1)",
  "paddingLeft": "0.8rem",
  "paddingRight": "0.8rem",
  "--tw-backdrop-blur": "blur(24px)",
  "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});
var _StyledDiv15 = _styled16("div").withConfig({
  componentId: "sc-sf-1ftbzj0-15"
})({
  "display": "flex",
  "gap": "0.8rem"
});
var _StyledButton = _styled17("button").withConfig({
  componentId: "sc-sf-1ftbzj0-16"
})`${p => p.$_css6}`;
var _StyledDiv16 = _styled18("div").withConfig({
  componentId: "sc-sf-1ftbzj0-17"
})({
  "display": "flex",
  "gap": "1.6rem"
});
var _StyledButton2 = _styled19("button").withConfig({
  componentId: "sc-sf-1ftbzj0-18"
})`${p => p.$_css7}`;
var _StyledSpan = _styled20("span").withConfig({
  componentId: "sc-sf-1ftbzj0-19"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledSpan2 = /*#__PURE__*/_styled21("span").withConfig({
  displayName: "secondary-hero___StyledSpan2",
  componentId: "sc-1nvkm2d-0"
})(["", ""], discoverBounce);